"use client";

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import * as Sentry from "@sentry/react";
import { ChevronDown, Clock, PanelRightOpen, RotateCcw, X } from "lucide-react";
import { LoadingState } from "@/components/brand";
import { PdfPreview } from "@/components/common/PdfPreview";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { StatusBadge } from "@/components/ui/status-badge";
import { cn } from "@/lib/utils";
import { CasePrepUscisFormsAPI } from "../api";
import type { CasePrepUscisFormVersion } from "../types";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  individualId: string | null;
  filledFormId: string | null;
  formTitle: string;
  versions: CasePrepUscisFormVersion[];
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  error: string | null;
  restoringVersionId: string | null;
  onRestoreClick: (version: CasePrepUscisFormVersion) => void;
};

const toDateMs = (value: unknown): number | null => {
  if (value == null) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string") {
    const ms = Date.parse(value);
    return Number.isFinite(ms) ? ms : null;
  }
  if (value instanceof Date) return value.getTime();
  if (
    typeof value === "object" &&
    value !== null &&
    "toDate" in value &&
    typeof (value as { toDate?: unknown }).toDate === "function"
  ) {
    try {
      const d = (value as { toDate: () => unknown }).toDate();
      if (d instanceof Date) return d.getTime();
    } catch {
      // ignore
    }
  }
  return null;
};

const formatAbsoluteTime = (value: unknown): string => {
  const ms = toDateMs(value);
  if (!ms) return "";
  try {
    return new Date(ms).toLocaleString();
  } catch {
    return "";
  }
};

const formatRelativeTime = (value: unknown): string => {
  const ts = toDateMs(value);
  if (!ts) return "";

  const diffMs = Math.max(0, Date.now() - ts);
  // Only show relative time for very recent events (<= 10 minutes).
  if (diffMs > 10 * 60_000) {
    return formatAbsoluteTime(value);
  }
  if (diffMs < 60_000) {
    const diffSec = Math.max(1, Math.floor(diffMs / 1000));
    return `${diffSec} sec${diffSec === 1 ? "" : "s"} ago`;
  }
  const diffMin = Math.floor(diffMs / 60_000);
  return `${diffMin} min${diffMin === 1 ? "" : "s"} ago`;
};

const getDisplayName = (v: CasePrepUscisFormVersion): string => {
  const p = v.created_by_profile;
  const first = (p?.first_name ?? "").trim();
  const last = (p?.last_name ?? "").trim();
  const full = `${first} ${last}`.trim();
  if (full) return full;
  const email = (p?.email ?? "").trim();
  if (email) return email;
  const denormalized = (v.created_by_name ?? "").trim();
  if (denormalized) return denormalized;
  const uid = (p?.uid ?? v.created_by ?? "").trim();
  return uid || "Unknown";
};

const getAvatarInitial = (name: string): string => {
  const trimmed = name.trim();
  if (!trimmed) return "?";
  return trimmed[0].toUpperCase();
};

const getVersionSubtitle = (v: CasePrepUscisFormVersion): string => {
  switch (v.trigger) {
    case "waterfall_run":
      return "Autofilled";
    case "manual_save":
      return "Manual save";
    case "blank_init":
      return "Started blank";
    case "pre_restore":
      return "Snapshot before restore";
    case "restore":
      return "Restored";
    default:
      return "Updated";
  }
};

export function FormVersionHistoryDrawer({
  open,
  onOpenChange,
  individualId,
  filledFormId,
  formTitle,
  versions,
  isLoading,
  isLoadingMore,
  hasMore,
  onLoadMore,
  error,
  restoringVersionId,
  onRestoreClick
}: Props) {
  const sortedVersions = useMemo(() => {
    const copy = [...versions];
    copy.sort((a, b) => {
      const aMs = toDateMs(a.created_at);
      const bMs = toDateMs(b.created_at);
      if (aMs != null && bMs != null) return bMs - aMs;
      if (aMs != null) return -1;
      if (bMs != null) return 1;
      return a.version_id.localeCompare(b.version_id);
    });
    return copy;
  }, [versions]);

  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(
    null
  );
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const seqRef = useRef(0);
  const isPreviewOpen = Boolean(selectedVersionId);

  const versionsScrollRef = useRef<HTMLDivElement | null>(null);
  const [showVersionsScrollHint, setShowVersionsScrollHint] = useState(false);
  const versionsHintRafRef = useRef<number | null>(null);

  const updateVersionsScrollHint = useCallback(() => {
    const el = versionsScrollRef.current;
    if (!el) return;
    const canScroll = el.scrollHeight - el.clientHeight > 8;
    const atTop = el.scrollTop <= 6;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 6;
    setShowVersionsScrollHint(canScroll && atTop && !atBottom);
  }, []);

  useEffect(() => {
    if (!open) return () => undefined;

    if (versionsHintRafRef.current) {
      window.cancelAnimationFrame(versionsHintRafRef.current);
      versionsHintRafRef.current = null;
    }

    const startedAt = window.performance?.now?.() ?? Date.now();
    const maxMs = 2000;

    const tick = () => {
      updateVersionsScrollHint();
      const now = window.performance?.now?.() ?? Date.now();
      if (now - startedAt < maxMs) {
        versionsHintRafRef.current = window.requestAnimationFrame(tick);
      } else {
        versionsHintRafRef.current = null;
      }
    };

    versionsHintRafRef.current = window.requestAnimationFrame(tick);
    window.addEventListener("resize", updateVersionsScrollHint);

    return () => {
      if (versionsHintRafRef.current) {
        window.cancelAnimationFrame(versionsHintRafRef.current);
        versionsHintRafRef.current = null;
      }
      window.removeEventListener("resize", updateVersionsScrollHint);
    };
  }, [open, isLoading, sortedVersions.length, updateVersionsScrollHint]);

  const selectedVersion = useMemo(() => {
    if (!selectedVersionId) return null;
    return (
      sortedVersions.find((v) => v.version_id === selectedVersionId) ?? null
    );
  }, [sortedVersions, selectedVersionId]);

  const viewingAtLabel = useMemo(() => {
    if (!selectedVersion) return "";
    return formatAbsoluteTime(selectedVersion.created_at);
  }, [selectedVersion]);

  const viewingFormName = useMemo(() => {
    const t = (formTitle ?? "").trim();
    return t || "Form";
  }, [formTitle]);

  useEffect(() => {
    if (open) return;
    // Reset selection + preview state when closing.
    setSelectedVersionId(null);
    setIsPreviewLoading(false);
    setPreviewError(null);
    setPdfBlob(null);
  }, [open]);

  useEffect(() => {
    if (!open || !individualId || !filledFormId || !selectedVersionId) {
      setIsPreviewLoading(false);
      setPreviewError(null);
      setPdfBlob(null);
      return;
    }

    seqRef.current += 1;
    const seq = seqRef.current;
    setIsPreviewLoading(true);
    setPreviewError(null);
    setPdfBlob(null);

    CasePrepUscisFormsAPI.getFilledFormVersionPdf(
      individualId,
      filledFormId,
      selectedVersionId
    )
      .then((blob) => {
        if (seqRef.current !== seq) return;
        setPdfBlob(blob);
      })
      .catch((e) => {
        if (seqRef.current !== seq) return;
        Sentry.captureException(e);
        setPreviewError("Failed to load version preview.");
      })
      .finally(() => {
        if (seqRef.current === seq) setIsPreviewLoading(false);
      });
  }, [individualId, filledFormId, open, selectedVersionId]);

  const previewMessage = (title: string, body: string, className?: string) => (
    <div
      className={cn(
        "flex w-full flex-col items-center justify-center gap-1 px-4 text-center",
        className
      )}
    >
      <span className="text-base font-medium text-fg">{title}</span>
      <span className="text-sm text-fg-3">{body}</span>
    </div>
  );

  return (
    <Sheet
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onOpenChange(false);
      }}
    >
      <SheetContent
        side="right"
        showCloseButton={false}
        overlayClassName="z-2147483646"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        className="z-2147483647 h-screen max-h-screen w-screen min-w-0 gap-0 border-l-0 bg-transparent p-0 shadow-none data-[side=right]:w-screen data-[side=right]:border-l-0 data-[side=right]:sm:max-w-none"
      >
        <SheetTitle className="sr-only">Version History</SheetTitle>
        <SheetDescription className="sr-only">
          View and restore previous versions of this form.
        </SheetDescription>
        <style>{`
          @keyframes lfFormVersionsScrollHintBounce {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(6px); }
          }
          .lf-form-versions-scroll-hint {
            animation: lfFormVersionsScrollHintBounce 1.1s ease-in-out infinite;
          }
          @media (prefers-reduced-motion: reduce) {
            .lf-form-versions-scroll-hint { animation: none; }
          }
        `}</style>
        <div
          className="flex h-full w-full min-w-0 items-stretch justify-end gap-1.5 p-2"
          onPointerDown={(e) => {
            if (!isPreviewOpen && e.target === e.currentTarget) {
              onOpenChange(false);
            }
          }}
        >
          {/* Left: PDF preview */}
          {isPreviewOpen ? (
            <div className="hidden h-full w-[min(820px,55vw)] min-w-0 flex-none items-stretch md:flex">
              <div className="h-full w-full min-w-0 overflow-hidden rounded-xl bg-surface shadow-dialog ring-1 ring-line-3">
                <div className="flex h-full min-h-0 w-full min-w-0 flex-col items-stretch">
                  <div className="relative flex min-h-0 w-full min-w-0 flex-1 flex-col">
                    <div className="absolute top-4 right-4 z-20">
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label="Close preview"
                        onClick={() => setSelectedVersionId(null)}
                      >
                        <X />
                      </Button>
                    </div>
                    {viewingAtLabel ? (
                      <div className="pointer-events-none absolute top-4 left-6 z-20">
                        <div className="flex items-center gap-2 rounded-md bg-elevated px-3 py-2 shadow-popover ring-1 ring-line-3">
                          <span className="text-sm font-medium text-fg">
                            Viewing {viewingFormName} at {viewingAtLabel}
                          </span>
                        </div>
                      </div>
                    ) : null}
                    <div
                      className={cn(
                        "min-h-0 w-full min-w-0 flex-1",
                        isPreviewLoading || previewError || !pdfBlob
                          ? "flex items-center justify-center overflow-hidden p-4"
                          : "overflow-hidden"
                      )}
                    >
                      {isPreviewLoading ? (
                        <LoadingState
                          size="xl"
                          className="w-full"
                          text="Loading preview…"
                        />
                      ) : previewError ? (
                        previewMessage("Preview Unavailable", previewError)
                      ) : pdfBlob ? (
                        <PdfPreview
                          file={pdfBlob}
                          ariaLabel={`${viewingFormName} version preview`}
                          defaultZoomMode="page-width"
                          showControls={false}
                          unavailableText="Failed to load version preview."
                        />
                      ) : (
                        <div className="flex h-full w-full flex-col items-center justify-center gap-2 px-4 text-center">
                          <span className="text-sm text-fg-3">
                            No file available to preview.
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {/* Right: Version list */}
          <div className="relative h-full w-[min(440px,92vw)] overflow-hidden rounded-xl bg-elevated shadow-dialog ring-1 ring-line-3">
            <div className="flex h-full w-full flex-col items-start">
              <div className="flex w-full items-center justify-start gap-3 border-b border-line-2 px-4 py-3">
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Close version history"
                  onClick={() => onOpenChange(false)}
                >
                  <PanelRightOpen />
                </Button>
                <div className="flex min-w-0 flex-col items-start">
                  <span className="truncate text-base font-medium text-fg">
                    Version History
                  </span>
                  <span className="truncate text-sm text-fg-3">
                    View and restore previous versions of this form.
                  </span>
                </div>
              </div>

              <div
                ref={versionsScrollRef}
                className="flex w-full shrink-0 grow basis-0 flex-col items-start divide-y divide-line-1 overflow-y-auto"
                onScroll={updateVersionsScrollHint}
              >
                {isLoading ? (
                  <div className="flex w-full flex-col items-stretch divide-y divide-line-1">
                    {Array.from({ length: 6 }).map((_, idx) => (
                      <div
                        // eslint-disable-next-line react/no-array-index-key
                        key={`form_version_skel_${idx}`}
                        className="flex w-full flex-col items-stretch px-6 py-4"
                      >
                        <div className="flex w-full items-center justify-between">
                          <div className="flex items-center gap-2">
                            {idx === 0 ? (
                              <Skeleton className="h-3 w-24" />
                            ) : null}
                            <Skeleton className="h-3 w-18" />
                          </div>
                          <Skeleton className="h-3 w-21" />
                        </div>
                        <div className="flex w-full items-center gap-3 pt-3">
                          <Skeleton className="size-6 shrink-0 rounded-md" />
                          <div className="flex w-full flex-col items-stretch gap-2">
                            <Skeleton className="h-4 w-11/20" />
                            <Skeleton className="h-3 w-3/4" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : error ? (
                  <div className="flex w-full flex-col items-start px-6 py-4">
                    <span className="text-sm text-danger">{error}</span>
                  </div>
                ) : sortedVersions.length === 0 ? (
                  <EmptyState
                    icon={Clock}
                    title="No Version History"
                    description="Version history will appear here as you autofill or edit this form."
                    className="w-full py-10"
                  />
                ) : (
                  sortedVersions.map((v, index) => {
                    const name = getDisplayName(v);
                    const initial = getAvatarInitial(name);
                    const timeLabel = formatRelativeTime(v.created_at);
                    const isCurrent = index === 0;
                    const isSelected = selectedVersionId === v.version_id;
                    const message = getVersionSubtitle(v);
                    const avatarUrl =
                      v.created_by_profile?.avatar_data_url ?? undefined;

                    const isRestoring = restoringVersionId === v.version_id;
                    const canRestore =
                      Boolean(v.version_id) && !isRestoring && !isCurrent;

                    return (
                      <div
                        key={v.version_id}
                        className={cn(
                          "group flex w-full cursor-pointer flex-col items-start px-6 py-4 transition-colors duration-100",
                          isSelected ? "bg-fill-2" : "hover:bg-fill-1"
                        )}
                        onClick={() => setSelectedVersionId(v.version_id)}
                      >
                        <div className="flex w-full items-center justify-between">
                          <div className="flex items-center gap-2">
                            {isCurrent ? (
                              <StatusBadge tone="accent">
                                Current Version
                              </StatusBadge>
                            ) : isPreviewOpen && isSelected ? (
                              <StatusBadge tone="info">Now Viewing</StatusBadge>
                            ) : null}
                            {timeLabel ? (
                              <span className="text-sm text-fg-3">
                                {timeLabel}
                              </span>
                            ) : null}
                          </div>

                          {isCurrent ? null : (
                            <Button
                              className={cn(
                                "transition-opacity",
                                isSelected
                                  ? "opacity-100"
                                  : "opacity-0 group-hover:opacity-100"
                              )}
                              variant="secondary"
                              size="xs"
                              disabled={!canRestore}
                              onClick={(e) => {
                                e.stopPropagation();
                                onRestoreClick(v);
                              }}
                            >
                              {isRestoring ? (
                                <Spinner className="size-3" />
                              ) : (
                                <RotateCcw />
                              )}
                              Restore
                            </Button>
                          )}
                        </div>

                        <div className="flex w-full items-center justify-between pt-2">
                          <div className="flex items-center gap-3">
                            <Avatar size="sm">
                              {avatarUrl ? (
                                <AvatarImage src={avatarUrl} alt={name} />
                              ) : null}
                              <AvatarFallback>{initial}</AvatarFallback>
                            </Avatar>
                            <div className="flex flex-col items-start">
                              <span className="text-base font-medium text-fg">
                                {name}
                              </span>
                              <span className="text-sm text-fg-3">
                                {message}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}

                {hasMore && !isLoading && !error ? (
                  <div className="flex min-h-16 w-full items-center justify-center px-6 py-4">
                    <Button
                      variant="secondary"
                      size="xs"
                      disabled={isLoadingMore}
                      onClick={(e) => {
                        e.stopPropagation();
                        onLoadMore();
                      }}
                    >
                      {isLoadingMore ? <Spinner className="size-3" /> : null}
                      Load More
                    </Button>
                  </div>
                ) : null}
              </div>

              {showVersionsScrollHint ? (
                <div className="pointer-events-none absolute bottom-3 left-1/2 z-20 -translate-x-1/2">
                  <div className="lf-form-versions-scroll-hint flex size-8 items-center justify-center rounded-full bg-elevated opacity-90 shadow-popover ring-1 ring-line-3">
                    <ChevronDown className="size-3.5 text-fg" />
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default FormVersionHistoryDrawer;
